import React, { useState, useEffect } from 'react';
import { accountManagersAPI, AccountManager } from '../../../api/account-managers';

interface AccountManagerFilterProps {
  selectedAccountManager: string | null;
  onAccountManagerChange: (hubspotId: string | null, name: string) => void;
  isFiltering?: boolean;
}

const AccountManagerFilter: React.FC<AccountManagerFilterProps> = ({
  selectedAccountManager,
  onAccountManagerChange,
  isFiltering = false,
}) => {
  const [accountManagers, setAccountManagers] = useState<AccountManager[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAccountManagers();
  }, []);

  const fetchAccountManagers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Every active account manager, page by page (DS pages start at 1).
      setAccountManagers(await accountManagersAPI.getAllActiveAccountManagers());
    } catch (err) {
      console.error('Error fetching account managers:', err);
      setError('Failed to load account managers');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    let name = 'All Account Managers';

    if (value) {
      const manager = accountManagers.find(m => m.hubspotId === value);
      if (manager) {
        name = `${manager.firstName} ${manager.lastName}`;
      }
    }

    onAccountManagerChange(value === '' ? null : value, name);
  };

  if (error) {
    return (
      <div className="text-sm text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <label htmlFor="account-manager-filter" className="text-sm font-medium text-gray-700">
        Account Manager:
      </label>
      <div className="relative">
        <select
          id="account-manager-filter"
          value={selectedAccountManager || ''}
          onChange={handleChange}
          disabled={loading || isFiltering}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed min-w-[250px]"
        >
          <option value="">All Account Managers</option>
          {loading ? (
            <option disabled>Loading...</option>
          ) : (
            accountManagers.map((manager) => (
              <option key={manager.id} value={manager.hubspotId}>
                {manager.firstName} {manager.lastName} ({manager.email})
              </option>
            ))
          )}
        </select>
        {isFiltering && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>
      {selectedAccountManager && !isFiltering && (
        <button
          onClick={() => onAccountManagerChange(null, 'All Account Managers')}
          className="text-sm text-gray-600 hover:text-gray-800 underline"
        >
          Clear
        </button>
      )}
      {isFiltering && (
        <span className="text-sm text-blue-600 font-medium">Filtering...</span>
      )}
    </div>
  );
};

export default AccountManagerFilter;
